"use client";

import { useEffect } from "react";
import { APP_UPDATED_MESSAGE, APP_UPDATED_TITLE, isStaleDeployError } from "@/lib/stale-deploy";

import { reportClientIssue } from "@/components/system/ErrorCapture";
import { classifyClientError } from "@/lib/issues/classify";

type BoundaryError = Error & { digest?: string };

export function reportSupplySegmentError(error: BoundaryError): void {
  const errorMessage = error.message || "Supply render error";
  const stack = error.stack ?? null;
  // classifyClientError already grades transport blips (/load failed|failed to
  // fetch|networkerror/i) as LOW severity — outlet WiFi noise must not page as
  // a HIGH crash (the cu auto-issue 99ce09ab lesson).
  const verdict = classifyClientError(errorMessage, stack);
  if (verdict.drop) return;

  void reportClientIssue({
    errorMessage,
    stack,
    severity: verdict.severity,
    // IssueSurface has no "supply" member — "ops" per the triage/ask
    // precedent (SURFACE_REGISTRY contract); the boundary context below
    // carries the REAL surface identity for triage.
    surface: "ops",
    context: {
      boundary: "supply-segment",
      digest: error.digest ?? null,
    },
  });
}

/**
 * Supply (`/supply`) route error boundary — cu 6e6770c33 ported.
 *
 * Without a segment boundary, one transient mobile fetch blip ("Load failed"
 * on outlet WiFi) escapes every nested boundary, hits the root
 * `global-error.tsx`, and replaces the installed PWA with the desktop ops
 * crash page. This keeps the failure scoped: the supply layout (dark canvas,
 * DS tokens) stays alive, the operator gets a thumb-sized "Try again" that
 * re-runs the render once signal returns, and transient blips report LOW.
 */
export default function SupplySegmentError({
  error,
  retry,
}: {
  error: BoundaryError;
  retry: () => void;
}) {
  useEffect(() => {
    reportSupplySegmentError(error);
  }, [error]);
  // A deploy landed while this page was open: its Server Actions no longer
  // exist, so "Try again" re-renders the same stale page forever. Only a real
  // reload fetches the new version (same branch as app/cu/error.tsx).
  const stale = isStaleDeployError(error);

  return (
    <main
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: "24px",
        background: "var(--canvas)",
        color: "var(--text)",
      }}
    >
      <section
        style={{
          width: "min(420px, 100%)",
          borderRadius: "18px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          padding: "22px",
          textAlign: "center",
          boxShadow: "var(--shadow-card)",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "var(--accent-text)",
            fontSize: "11px",
            fontWeight: 800,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}
        >
          Supply
        </p>
        <h1
          style={{
            margin: "10px 0 0",
            color: "var(--text)",
            fontSize: "21px",
            fontWeight: 800,
            lineHeight: 1.15,
          }}
        >
          {stale ? APP_UPDATED_TITLE : "Connection dropped"}
        </h1>
        <p
          style={{
            margin: "10px 0 0",
            color: "var(--muted)",
            fontSize: "13.5px",
            lineHeight: 1.55,
            fontWeight: 600,
          }}
        >
          {stale
            ? APP_UPDATED_MESSAGE
            : "Couldn't load this screen — tap to retry. Nothing you entered was lost."}
        </p>
        <button
          type="button"
          onClick={stale ? () => window.location.reload() : retry}
          style={{
            marginTop: "18px",
            width: "100%",
            height: "54px",
            border: "none",
            borderRadius: "16px",
            background: "var(--amber)",
            color: "#16161D",
            fontSize: "15px",
            fontWeight: 800,
            WebkitTapHighlightColor: "transparent",
            touchAction: "manipulation",
          }}
        >
          {stale ? "Reload" : "Try again"}
        </button>
      </section>
    </main>
  );
}
